// src/api/couleursMetier.api.ts — SCE Issues
// Lecture / écriture des couleurs métier (Lot 8) dans SCE_UserPrefs.
//
// ─── Pourquoi SCE_UserPrefs (décision 10-C du cadrage) ───
//
// La liste a déjà le schéma voulu — `Title` = clé, `Prefs` = JSON versionné —
// et une ligne de plus ne coûte rien. Une liste dédiée aurait demandé un
// nouveau GUID dans .env.local ET dans les secrets GitHub, pour une ligne.
//
// La ligne des réglages globaux a pour clé `__global__`. Un UPN contient
// toujours « @ » : aucune collision possible avec la ligne d'un utilisateur,
// et la présence (listerPresents) l'écarte pour la même raison.
//
// Seule colonne écrite : `Prefs`, qui existe. Aucune action SharePoint.
//
// ⚠️ Le JSON de la ligne peut porter d'autres réglages globaux à l'avenir :
//    l'écriture relit la ligne et ne remplace QUE le bloc `couleursMetier`.

import { graphPatch, graphPost } from './graphClient'
import { CLE_REGLAGES_GLOBAUX, cheminReglages, lireLigneGlobale, reglagesConfigures } from './reglagesGlobaux.api'
import type { Metier } from '@/types/issue.types'
import {
  REGLAGE_VIDE,
  VERSION_REGLAGE,
  changerCouleur,
  lireReglage,
  messageRefus,
  type ReglageCouleurs,
} from '@/utils/couleursMetier'

/** Les couleurs choisies. Aucune ligne → réglage vide (couleurs d'origine). */
export async function lireReglageCouleurs(): Promise<ReglageCouleurs> {
  if (!reglagesConfigures()) return REGLAGE_VIDE
  const { json } = await lireLigneGlobale()
  return lireReglage(json['couleursMetier'])
}

/**
 * Change la couleur d'un métier. Relit la ligne juste avant d'écrire : si la
 * teinte vient d'être prise par un autre métier, rien n'est écrit et l'erreur
 * le dit. Les droits sont vérifiés par l'appelant (`peutChangerCouleur`) ;
 * SharePoint, lui, n'en sait rien.
 *
 * @param par  Nom du compte RÉEL (bascule de rôle : jamais la personne affichée).
 * @returns le réglage tel qu'écrit.
 */
export async function enregistrerCouleurMetier(
  metier: Metier,
  hex: string,
  par: string,
): Promise<ReglageCouleurs> {
  const ligne = await lireLigneGlobale()
  const actuel = lireReglage(ligne.json['couleursMetier'])
  const resultat = changerCouleur(actuel, metier, hex, par, new Date().toISOString())
  if (!resultat.ok) throw new Error(messageRefus(resultat.refus))

  const prefs = JSON.stringify({
    ...ligne.json,
    version: VERSION_REGLAGE,
    couleursMetier: resultat.reglage.metiers,
  })

  if (ligne.itemId) {
    await graphPatch<unknown>(`${cheminReglages()}/items/${ligne.itemId}/fields`, { Prefs: prefs })
  } else {
    await graphPost<unknown>(`${cheminReglages()}/items`, {
      fields: { Title: CLE_REGLAGES_GLOBAUX, Prefs: prefs },
    })
  }
  return resultat.reglage
}
